import { request } from '@/lib/api'

/**
 * Оценка лида (#129): `/sales/leads/{id}/score` и отчёт о модели `/sales/lead-scoring/model`.
 *
 * Сервер не присылает пустых полей вовсе (`non_null`): нет `probability` — значит её нет
 * (оценка правилом), а не ноль; нет `score` — лид ещё не оценён. Сравнивать с `== null`.
 */
export const RESOURCE = 'sales.leadscore'
export const MODEL_KEY = ['lead-score-model'] as const
export const scoreKey = (leadId: string) => ['lead-score', leadId] as const

export type ScoreClass = 'hot' | 'warm' | 'cold'
export type ModelStatus = 'trained' | 'insufficient_data' | 'none'
export type TextStatus = 'analyzed' | 'no_text' | 'pending' | 'unavailable' | 'failed' | 'rejected'

export interface ScoreFactor {
  code: string
  /** Признак: да/нет, число слов, код источника или намерение текста. */
  value?: boolean | number | string | null
  /** Вклад: баллы правила или слагаемое логарифма шансов модели; знак — направление. */
  contribution: number
  kind: 'model' | 'rule' | 'text'
}

export interface TextReading {
  status: TextStatus
  intent?: 'high' | 'medium' | 'low' | 'none'
  urgent?: boolean
  budgetMentioned?: boolean
  spam?: boolean
  reason?: string | null
}

export interface LeadScore {
  leadId: string
  scoreClass?: ScoreClass | null
  /** Только у оценки моделью: у правила вероятности нет — это не ноль. */
  probability?: number | null
  method?: 'model' | 'rule' | null
  points?: number | null
  textStatus?: TextStatus | null
  text?: TextReading | null
  factors?: ScoreFactor[]
  notes?: string[]
  scoredAt?: string | null
  distributionDeferred?: boolean
}

export interface LeadScoreResponse {
  leadId: string
  modelStatus: ModelStatus
  score?: LeadScore | null
}

export function fetchLeadScore(leadId: string, signal?: AbortSignal): Promise<LeadScoreResponse> {
  return request<LeadScoreResponse>(`/sales/leads/${leadId}/score`, { signal })
}

export function rescoreLead(leadId: string): Promise<LeadScoreResponse> {
  return request<LeadScoreResponse>(`/sales/leads/${leadId}/score`, { method: 'POST', body: {} })
}

export interface Scores {
  auc?: number | null
  logLoss: number
  brier: number
}

export interface CalibrationBucket {
  from: number
  to: number
  count: number
  meanPredicted?: number | null
  actualRate?: number | null
}

export interface ModelMetrics {
  model: Scores
  /** «Всегда средняя конверсия»: с ней сравниваются log-loss и Brier. */
  baseline: Scores
  /** AUC правила баллов — его ранжирование, с которым сравнивается модель. */
  ruleAuc?: number | null
  calibration: CalibrationBucket[]
  trainConversionRate: number
  holdoutConversionRate: number
  /** Откуда признаки обучения: снимок при приёме, журнал создания или текущая строка. */
  origins: Record<string, number>
}

export interface Coefficient {
  code: string
  weight: number
}

export interface LeadModelReport {
  status: ModelStatus
  modelId?: string
  trainedAt?: string
  closedLeads?: number
  minClosedLeads?: number
  trainLeads?: number
  holdoutLeads?: number
  holdoutFrom?: string
  message?: string
  metrics?: ModelMetrics
  coefficients: Coefficient[]
  sources: string[]
  textLearned?: boolean
  scoredLeads: Record<string, number>
  configuredMinClosedLeads: number
  hotThreshold: number
  warmThreshold: number
}

export function fetchLeadModel(signal?: AbortSignal): Promise<LeadModelReport> {
  return request<LeadModelReport>('/sales/lead-scoring/model', { signal })
}

export function trainLeadModel(): Promise<LeadModelReport> {
  return request<LeadModelReport>('/sales/lead-scoring/model/train', { method: 'POST', body: {} })
}
